import React, { useState, useEffect } from 'react';
import { adminSettingsAPI } from '../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Unified Feature Flags Section Component
const FeatureFlagsSection: React.FC = () => {
  const [features, setFeatures] = useState<any[]>([]);
  const [aggregate, setAggregate] = useState<any>(null);
  const [previousMonthAggregate, setPreviousMonthAggregate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savingPricing, setSavingPricing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);
  const [pricingForms, setPricingForms] = useState<Record<string, any>>({});
  const [previewEnabled, setPreviewEnabled] = useState<Record<string, boolean>>({});
  const [previewPricing, setPreviewPricing] = useState<Record<string, any>>({});
  const [monthlyBreakdown, setMonthlyBreakdown] = useState<any>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  // Filter state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [showSpecificMonth, setShowSpecificMonth] = useState(false);

  const loadMonthlyBreakdown = async () => {
    try {
      setLoadingBreakdown(true);
      const response = await adminSettingsAPI.getMonthlyCostBreakdown(selectedYear);
      console.log('[Frontend] Monthly breakdown API response:', response);
      console.log('[Frontend] Response data:', response.data);
      if (response.data) {
        console.log('[Frontend] Monthly data length:', response.data.monthlyData?.length);
        console.log('[Frontend] Yearly totals:', {
          yearlyTotal: response.data.yearlyTotal,
          yearlyPlatformTotal: response.data.yearlyPlatformTotal,
          yearlyFeatureFlagTotal: response.data.yearlyFeatureFlagTotal
        });
        setMonthlyBreakdown(response.data);
      } else {
        console.error('[Frontend] No data in response:', response);
      }
    } catch (error: any) {
      console.error('Error loading monthly breakdown:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  useEffect(() => {
    loadFeatureFlags();
    if (!showSpecificMonth) {
      loadMonthlyBreakdown();
    }
  }, [selectedYear, selectedMonth, showSpecificMonth]);

  const loadFeatureFlags = async () => {
    try {
      setLoading(true);
      const params: any = { year: selectedYear };
      if (showSpecificMonth && selectedMonth !== null) {
        params.month = selectedMonth;
      }
      
      const [currentResponse, previousResponse] = await Promise.all([
        adminSettingsAPI.getAllFeatureFlags(params),
        adminSettingsAPI.getAllFeatureFlags({ ...params, previousMonth: true })
      ]);
      
      setFeatures(currentResponse.data.features || []);
      setAggregate(currentResponse.data.aggregate);
      setPreviousMonthAggregate(previousResponse.data.aggregate);
      
      // Initialize pricing forms with current settings
      const forms: Record<string, any> = {};
      currentResponse.data.features.forEach((feature: any) => {
        forms[feature.name] = {
          costPerUnit: feature.settings.costPerUnit || '',
          costPer1000Units: feature.settings.costPer1000Units || '',
          freeTierLimit: feature.settings.freeTierLimit || '',
          currency: feature.settings.currency || 'USD',
          exchangeRate: feature.settings.exchangeRate || 83
        };
      });
      setPricingForms(forms);
      setPreviewPricing(forms);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  // Calculate projected cost with preview changes
  const calculateProjectedCost = (feature: any, previewEnabledState: boolean, previewPricingState: any) => {
    if (!previewEnabledState) return { total: 0, currency: 'INR', formatted: '₹0.00' };
    
    const pricing = previewPricingState || pricingForms[feature.name] || feature.settings;
    const usage = feature.usage.thisMonth;
    const daysElapsed = Math.floor(
      (new Date().getTime() - new Date(feature.lastResetDate || new Date()).getTime()) / (1000 * 60 * 60 * 24)
    ) || 1;
    const dailyAverage = usage / daysElapsed;
    const projectedMonthly = dailyAverage * 30;

    let total = 0;
    if (pricing.costPerUnit !== undefined && pricing.costPerUnit > 0) {
      total = projectedMonthly * parseFloat(pricing.costPerUnit || 0);
    } else if (pricing.costPer1000Units !== undefined && pricing.costPer1000Units > 0) {
      const freeTierLimit = parseInt(pricing.freeTierLimit || 0);
      const billableUnits = Math.max(0, projectedMonthly - freeTierLimit);
      total = (billableUnits / 1000) * parseFloat(pricing.costPer1000Units || 0);
    }

    // Convert to INR
    const currency = pricing.currency || 'USD';
    const exchangeRate = parseFloat(pricing.exchangeRate || 83);
    const totalInINR = currency === 'INR' ? total : total * (exchangeRate > 1 ? exchangeRate : 83);

    return {
      total: totalInINR,
      currency: 'INR',
      formatted: `₹${totalInINR.toFixed(2)}`
    };
  };

  const handleToggle = async (featureName: string, newEnabled: boolean) => {
    // For preview mode, just update local state
    if (!previewEnabled[featureName]) {
      setPreviewEnabled({ ...previewEnabled, [featureName]: newEnabled });
      return;
    }

    try {
      setSaving(featureName);
      setError('');
      setSuccess('');
      
      const response = await adminSettingsAPI.toggleFeatureFlag(featureName, newEnabled);
      setFeatures(response.data.features || []);
      setAggregate(response.data.aggregate);
      setPreviewEnabled({ ...previewEnabled, [featureName]: false });
      setSuccess(`Feature ${featureName} ${newEnabled ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to toggle feature');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveAll = async () => {
    try {
      setError('');
      setSuccess('');
      
      // Save all enabled state changes
      for (const [featureName, enabled] of Object.entries(previewEnabled)) {
        if (enabled !== undefined) {
          const feature = features.find(f => f.name === featureName);
          if (feature && feature.enabled !== enabled) {
            await adminSettingsAPI.toggleFeatureFlag(featureName, enabled);
          }
        }
      }

      // Save all pricing changes
      for (const [featureName, pricing] of Object.entries(previewPricing)) {
        const originalPricing = pricingForms[featureName];
        if (JSON.stringify(pricing) !== JSON.stringify(originalPricing)) {
          await handleUpdatePricing(featureName, pricing);
        }
      }

      // Reload data
      await loadFeatureFlags();
      setPreviewEnabled({});
      setPreviewPricing(pricingForms);
      setSuccess('All changes saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save changes');
    }
  };

  const handleUpdatePricing = async (featureName: string, pricingToUse?: any) => {
    try {
      setSavingPricing(featureName);
      setError('');
      setSuccess('');
      
      const pricing = pricingToUse || pricingForms[featureName];
      const pricingData: any = {};
      
      // Build pricing data based on feature type
      if (featureName === 'messageCentralOTP' && pricing.costPerUnit) {
        pricingData.costPerOTP = parseFloat(pricing.costPerUnit);
      } else if (featureName === 'googleMapsAPI') {
        if (pricing.costPer1000Units) pricingData.costPer1000Requests = parseFloat(pricing.costPer1000Units);
        if (pricing.freeTierLimit) pricingData.freeTierLimit = parseInt(pricing.freeTierLimit);
      } else if (featureName === 'firebasePushNotifications' && pricing.costPerUnit) {
        pricingData.costPerNotification = parseFloat(pricing.costPerUnit);
      } else if (featureName === 'emailService') {
        if (pricing.costPer1000Units) pricingData.costPer1000Emails = parseFloat(pricing.costPer1000Units);
        if (pricing.freeTierLimit) pricingData.freeTierLimit = parseInt(pricing.freeTierLimit);
      } else if (featureName === 'sentry') {
        if (pricing.costPer1000Units) pricingData.costPer1000Events = parseFloat(pricing.costPer1000Units);
        if (pricing.freeTierLimit) pricingData.freeTierLimit = parseInt(pricing.freeTierLimit);
      }
      
      if (pricing.currency) pricingData.currency = pricing.currency;
      if (pricing.exchangeRate) pricingData.exchangeRate = parseFloat(pricing.exchangeRate);

      const response = await adminSettingsAPI.updateFeatureFlagPricing(featureName, pricingData);
      setFeatures(response.data.features || []);
      setAggregate(response.data.aggregate);
      setSuccess(`Pricing updated for ${featureName}!`);
      setExpandedFeature(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update pricing');
    } finally {
      setSavingPricing(null);
    }
  };

  const handlePricingChange = (featureName: string, field: string, value: any) => {
    setPreviewPricing({
      ...previewPricing,
      [featureName]: {
        ...previewPricing[featureName],
        ...pricingForms[featureName],
        [field]: value
      }
    });
  };

  const getFeatureDisplayName = (name: string): string => {
    const names: { [key: string]: string } = {
      messageCentralOTP: 'MessageCentral OTP',
      googleMapsAPI: 'Google Maps API',
      firebasePushNotifications: 'Firebase Push Notifications',
      emailService: 'Email Service',
      sentry: 'Sentry Error Monitoring'
    };
    return names[name] || name;
  };

  const getFeatureDescription = (name: string): string => {
    const descriptions: { [key: string]: string } = {
      messageCentralOTP: 'OTP verification via MessageCentral VerifyNow API',
      googleMapsAPI: 'Distance calculations, geocoding, and places search',
      firebasePushNotifications: 'Push notifications to mobile devices',
      emailService: 'Email notifications (booking receipts, alerts)',
      sentry: 'Error monitoring and performance tracking'
    };
    return descriptions[name] || '';
  };

  if (loading) {
    return <div className="text-center py-4">Loading feature flags...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 rounded"
            >
              {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <input
                type="checkbox"
                checked={showSpecificMonth}
                onChange={(e) => {
                  setShowSpecificMonth(e.target.checked);
                  if (!e.target.checked) setSelectedMonth(null);
                }}
                className="mr-2"
              />
              Show Specific Month
            </label>
            {showSpecificMonth && (
              <select
                value={selectedMonth ?? ''}
                onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-2 text-sm border border-gray-300 rounded mt-1"
              >
                <option value="">Select Month</option>
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
                  <option key={idx} value={idx}>{month}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Cost Graph */}
      {monthlyBreakdown && !showSpecificMonth && (
        <div className="p-4 bg-white rounded-lg border border-gray-200 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Monthly Cost Breakdown for {selectedYear} (All in INR)
          </h3>
          {loadingBreakdown ? (
            <div className="text-center py-4">Loading graph...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Yearly Total</p>
                  <p className="text-xl font-bold text-black">{monthlyBreakdown.yearlyTotal.formatted}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Platform Costs</p>
                  <p className="text-xl font-bold text-black">{monthlyBreakdown.yearlyPlatformTotal.formatted}</p>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Feature Flags</p>
                  <p className="text-xl font-bold text-black">{monthlyBreakdown.yearlyFeatureFlagTotal.formatted}</p>
                </div>
              </div>
              
              {/* Line Chart */}
              <div className="mt-6">
                {(() => {
                  const chartData = monthlyBreakdown.monthlyData.map((m: any) => ({
                    month: m.month,
                    platform: m.platformCost.total,
                    features: m.featureFlagCost.total,
                    total: m.total,
                    formatted: m.formatted,
                    platformFormatted: m.platformCost.formatted,
                    featuresFormatted: m.featureFlagCost.formatted
                  }));
                  
                  const maxValue = Math.max(...chartData.map((d: any) => d.total), 1);
                  const minValue = Math.min(...chartData.map((d: any) => d.total), 0);
                  const niceMax = Math.ceil(maxValue * 1.1);
                  const niceMin = Math.max(0, Math.floor(minValue * 0.9));
                  
                  const formatYAxis = (value: number) => {
                    if (value >= 1000) {
                      return `₹${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
                    }
                    return `₹${value.toFixed(0)}`;
                  };
                  
                  return (
                    <div style={{ width: '100%', height: '350px' }}>
                      <ResponsiveContainer width="100%" height={350}>
                        <LineChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 50, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.6} />
                          <XAxis 
                            dataKey="month" 
                            tick={{ fontSize: 12, fill: '#6b7280' }}
                            label={{ value: 'Month', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
                          />
                          <YAxis 
                            domain={[niceMin, niceMax]}
                            tick={{ fontSize: 12, fill: '#6b7280' }}
                            tickFormatter={formatYAxis}
                            label={{ value: 'Cost (₹)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 } }}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-gray-900 text-white text-xs rounded py-2 px-3">
                                    <div className="mb-1 font-semibold">{data.month} {selectedYear}</div>
                                    <div>Total: {data.formatted}</div>
                                    <div>Platform: {data.platformFormatted}</div>
                                    <div>Features: {data.featuresFormatted}</div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            iconType="line"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="platform" 
                            stroke="#a855f7" 
                            strokeWidth={2}
                            dot={{ fill: '#a855f7', r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                            activeDot={{ r: 8 }}
                            name="Platform Costs"
                            opacity={0.7}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="features" 
                            stroke="#22c55e" 
                            strokeWidth={2}
                            dot={{ fill: '#22c55e', r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                            activeDot={{ r: 8 }}
                            name="Feature Flags"
                            opacity={0.7}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="total" 
                            stroke="#3b82f6" 
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            dot={{ fill: '#3b82f6', r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                            activeDot={{ r: 7 }}
                            name="Total (dashed)"
                            opacity={0.6}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                      {/* Month values below chart */}
                      <div className="flex justify-between mt-2 px-4">
                        {chartData.map((data: any, index: number) => (
                          <div key={index} className="flex flex-col items-center" style={{ flex: 1 }}>
                            <div className="text-xs font-bold text-black">{data.formatted}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Aggregate Cost Summary */}
      {aggregate && (
        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Total Cost Summary (Feature Flags + Fixed Costs) - All in INR
          </h3>
          <div className="grid grid-cols-4 gap-4 mb-3">
            <div className="bg-white p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">
                {showSpecificMonth && selectedMonth !== null 
                  ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][selectedMonth]} ${selectedYear}`
                  : 'This Month'}
              </p>
              <p className="text-xl font-bold text-black">{aggregate.totalThisMonth.formatted}</p>
            </div>
            {previousMonthAggregate && (
              <div className="bg-white p-3 rounded">
                <p className="text-xs text-gray-600 mb-1">Previous Month</p>
                <p className="text-xl font-bold text-black">{previousMonthAggregate.totalThisMonth.formatted}</p>
              </div>
            )}
            <div className="bg-white p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">All Time</p>
              <p className="text-xl font-bold text-black">{aggregate.totalAllTime.formatted}</p>
            </div>
            <div className="bg-white p-3 rounded">
              <p className="text-xs text-gray-600 mb-1">Projected Monthly</p>
              <p className="text-xl font-bold text-black">{aggregate.projectedMonthly.formatted}</p>
            </div>
          </div>
          {aggregate.breakdown && (
            <div className="mt-3 pt-3 border-t border-blue-300">
              <p className="text-xs text-gray-600 mb-2">Cost Breakdown:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Feature Flags (This Month): </span>
                  <span className="font-semibold">
                    ₹{aggregate.breakdown.featureFlags?.thisMonth?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Fixed Costs (Monthly): </span>
                  <span className="font-semibold">
                    ₹{aggregate.breakdown.platform?.monthly?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save All Button for Preview Changes */}
      {(Object.keys(previewEnabled).length > 0 || Object.keys(previewPricing).some(key => 
        JSON.stringify(previewPricing[key]) !== JSON.stringify(pricingForms[key])
      )) && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800 mb-2">
            You have unsaved changes. Click "Save All Changes" to apply them.
          </p>
          <button
            onClick={handleSaveAll}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            Save All Changes
          </button>
        </div>
      )}

      {/* Individual Feature Flags */}
      <div className="space-y-4">
        {features.map((feature) => (
          <div key={feature.name} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                  {getFeatureDisplayName(feature.name)}
                </h4>
                <p className="text-xs text-gray-600 mb-2">{getFeatureDescription(feature.name)}</p>
                
                {/* Usage Stats */}
                <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                  <div>
                    <span className="text-gray-600">Total: </span>
                    <span className="font-semibold">{feature.usage.total.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">This Month: </span>
                    <span className="font-semibold">{feature.usage.thisMonth.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Daily Avg: </span>
                    <span className="font-semibold">{feature.usage.dailyAverage}</span>
                  </div>
                </div>

                {/* Cost Display */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Total Cost: </span>
                    <span className="font-semibold text-black">{feature.costs.total.formatted}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">This Month: </span>
                    <span className="font-semibold text-black">{feature.costs.thisMonth.formatted}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Projected: </span>
                    <span className="font-semibold text-black">
                      {(() => {
                        const previewState = previewEnabled[feature.name] !== undefined 
                          ? previewEnabled[feature.name] 
                          : feature.enabled;
                        const previewPricingState = previewPricing[feature.name] || pricingForms[feature.name];
                        const projected = calculateProjectedCost(feature, previewState, previewPricingState);
                        return projected.formatted;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Toggle Switch */}
              <div className="ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={previewEnabled[feature.name] !== undefined ? previewEnabled[feature.name] : feature.enabled}
                    onChange={(e) => handleToggle(feature.name, e.target.checked)}
                    disabled={saving === feature.name}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                </label>
                {previewEnabled[feature.name] !== undefined && previewEnabled[feature.name] !== feature.enabled && (
                  <span className="text-xs text-yellow-600 block mt-1">Preview</span>
                )}
              </div>
            </div>

            {/* Cost Settings Info */}
            {feature.settings && (
              <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                {feature.settings.costPerUnit && (
                  <span>Cost: {feature.settings.costPerUnit} {feature.settings.currency} per unit</span>
                )}
                {feature.settings.costPer1000Units && (
                  <span>
                    Cost: {feature.settings.costPer1000Units} {feature.settings.currency} per 1,000 units
                    {feature.settings.freeTierLimit && ` (${feature.settings.freeTierLimit.toLocaleString()} free/month)`}
                  </span>
                )}
              </div>
            )}

            {/* Pricing Configuration */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => setExpandedFeature(expandedFeature === feature.name ? null : feature.name)}
                className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-black"
              >
                {expandedFeature === feature.name ? '▼' : '▶'} Configure Pricing
              </button>
              
              {expandedFeature === feature.name && (
                <div className="mt-3 space-y-3 p-3 bg-white rounded border border-gray-200">
                  {feature.name === 'messageCentralOTP' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cost per OTP</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={pricingForms[feature.name]?.costPerUnit || ''}
                        onChange={(e) => setPricingForms({
                          ...pricingForms,
                          [feature.name]: { ...pricingForms[feature.name], costPerUnit: e.target.value }
                        })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="0.01"
                      />
                    </div>
                  )}
                  
                  {(feature.name === 'googleMapsAPI' || feature.name === 'emailService' || feature.name === 'sentry') && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Cost per 1,000 {feature.name === 'googleMapsAPI' ? 'requests' : feature.name === 'emailService' ? 'emails' : 'events'}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={previewPricing[feature.name]?.costPer1000Units || pricingForms[feature.name]?.costPer1000Units || ''}
                          onChange={(e) => handlePricingChange(feature.name, 'costPer1000Units', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="5.0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Free Tier Limit (per month)</label>
                        <input
                          type="number"
                          min="0"
                          value={previewPricing[feature.name]?.freeTierLimit || pricingForms[feature.name]?.freeTierLimit || ''}
                          onChange={(e) => handlePricingChange(feature.name, 'freeTierLimit', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          placeholder="200000"
                        />
                      </div>
                    </>
                  )}
                  
                  {feature.name === 'firebasePushNotifications' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cost per Notification</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={pricingForms[feature.name]?.costPerUnit || ''}
                        onChange={(e) => setPricingForms({
                          ...pricingForms,
                          [feature.name]: { ...pricingForms[feature.name], costPerUnit: e.target.value }
                        })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="0"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handlePricingChange(feature.name, 'currency', 'USD')}
                        className={`flex-1 px-3 py-2 text-xs rounded border ${
                          (previewPricing[feature.name]?.currency || pricingForms[feature.name]?.currency) === 'USD'
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        USD
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePricingChange(feature.name, 'currency', 'INR')}
                        className={`flex-1 px-3 py-2 text-xs rounded border ${
                          (previewPricing[feature.name]?.currency || pricingForms[feature.name]?.currency) === 'INR'
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        INR
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-2">
                    Changes are in preview mode. Use "Save All Changes" button above to save.
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={loadFeatureFlags}
        className="mt-4 px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        Refresh Data
      </button>
    </div>
  );
};

export default FeatureFlagsSection;
